import React from 'react';
import { format } from 'date-fns';
import Container from '@mui/material/Container';
import PermissionBasedGuard from 'src/auth/guard/permission-based-guard';
import { useAuthContext } from 'src/auth/hooks';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs/custom-breadcrumbs';
import { useGetSelectedFoodsIngredients, useGetRestaurantSelectedFoodsIngredients } from 'src/api/restaurant';
import { TableCell, TableRow, Card } from '@mui/material';
import { components } from 'src/types/schema';
import { DataTable, TableToolbar, useFilters, usePagination, Column } from './selected-food-table';

// ----------------------------------------------------------------------

// Types for the ingredients data
type IngredientCategory =
  components['schemas']['PartnersSubscriptionSelectedFoodIngredientsDataCategory'];
type IngredientData = {
  protein_categories: IngredientCategory[];
  carb_categories: IngredientCategory[];
};
type IngredientGroup = components['schemas']['PartnersSubscriptionSelectedFoodIngredientsList'];

// Custom types for our UI
type FlattenedIngredient = {
  id: string;
  ingredient: string;
  times_selected: number;
  total_quantity: number;
  unit: string;
  category: string;
};

// ----------------------------------------------------------------------

export default function SelectedFoodIngredientsListPage({ restaurantId }: { restaurantId?: string }) {
  const { user } = useAuthContext();
  const { filters, onFilters, onMultipleFilters, resetFilters, getDateRange } = useFilters(
    'selected_food_ingredients'
  );
  const { page, rowsPerPage, onPageChange, onRowsPerPageChange } = usePagination();

  const dateRange = getDateRange(filters.selectedDay);

  // Use restaurant-specific API if restaurantId is provided, otherwise use general API
  const { selectedIngredients, selectedIngredientsLoading, selectedIngredientsError } = restaurantId
    ? useGetRestaurantSelectedFoodsIngredients(
        restaurantId,
        filters.search || undefined,
        dateRange.date_gte,
        dateRange.date_lte
      )
    : useGetSelectedFoodsIngredients(
        filters.search || undefined,
        dateRange.date_gte,
        dateRange.date_lte
      );

  // Prepare data for display
  const prepareData = () => {
    if (!selectedIngredients) return [];

    // Flatten the data for client-side pagination
    const flattenedData = selectedIngredients.reduce(
      (
        acc: Array<{ group: IngredientGroup; ingredient: FlattenedIngredient }>,
        group: IngredientGroup
      ) => {
        // For each group, transform protein and carb categories into flattened ingredients
        group.data.protein_categories.forEach((protein: IngredientCategory, index: number) => {
          acc.push({
            group,
            ingredient: {
              id: `protein-${group.date}-${index}`,
              ingredient: protein.name,
              times_selected: 1, // Assuming a default value
              total_quantity: protein.grams,
              unit: 'g',
              category: 'Protein',
            },
          });
        });

        group.data.carb_categories.forEach((carb: IngredientCategory, index: number) => {
          acc.push({
            group,
            ingredient: {
              id: `carb-${group.date}-${index}`,
              ingredient: carb.name,
              times_selected: 1, // Assuming a default value
              total_quantity: carb.grams,
              unit: 'g',
              category: 'Carb',
            },
          });
        });

        return acc;
      },
      []
    );

    const paginatedData = flattenedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    // Group the paginated data back by date for display
    const groupedData = paginatedData.reduce(
      (
        acc: { [key: string]: { ingredient: FlattenedIngredient; index: number }[] },
        { group, ingredient }: { group: IngredientGroup; ingredient: FlattenedIngredient },
        idx: number
      ) => {
        if (!acc[group.date]) {
          acc[group.date] = [];
        }
        acc[group.date].push({ ingredient, index: idx });
        return acc;
      },
      {}
    );

    return Object.entries(groupedData).map(([date, ingredients]) => ({ date, ingredients }));
  };

  // Define columns for the table
  const columns: Column[] = [
    { id: 'ingredient', label: 'Ingredient', minWidth: 160 },
    { id: 'times_selected', label: 'Times Selected', minWidth: 120 },
    { id: 'total_quantity', label: 'Total Quantity', minWidth: 120 },
    { id: 'category', label: 'Category', minWidth: 120 },
  ];

  // Calculate total count for client-side pagination
  const totalCount =
    selectedIngredients?.reduce(
      (acc: number, group: IngredientGroup) =>
        acc + group.data.protein_categories.length + group.data.carb_categories.length,
      0
    ) || 0;

  if (!user?.restaurants[0]?.id) {
    return <div>No restaurant found</div>;
  }

  // Render table rows
  const renderRow = (
    item: { date: string; ingredients: { ingredient: FlattenedIngredient; index: number }[] },
    _groupIndex: number
  ) =>
    item.ingredients.map((ingredientItem, ingredientIndex) => (
      <TableRow
        key={`${item.date}-${ingredientItem.ingredient.id}`}
        sx={{
          '& td': {
            borderBottom:
              ingredientIndex === item.ingredients.length - 1
                ? '2px solid rgba(224, 224, 224, 1)'
                : undefined,
          },
        }}
      >
        <TableCell>{ingredientItem.ingredient.ingredient}</TableCell>
        <TableCell>{ingredientItem.ingredient.times_selected}</TableCell>
        <TableCell>{ingredientItem.ingredient.total_quantity}</TableCell>
        <TableCell>{ingredientItem.ingredient.category}</TableCell>
      </TableRow>
    ));

  return (
    <PermissionBasedGuard permissions={['partners:read:selectedfood']} hasContent>
      <Container maxWidth={false}>
        <CustomBreadcrumbs heading="Selected Food Ingredients" sx={{ mb: 2 }} links={[{}]} />

        <Card>
          <TableToolbar
            filters={filters}
            onFilters={onFilters}
            onMultipleFilters={onMultipleFilters}
            onResetFilters={resetFilters}
            searchPlaceholder="Search ingredients..."
          />

          <DataTable
            data={prepareData()}
            columns={columns}
            loading={selectedIngredientsLoading}
            error={selectedIngredientsError}
            page={page}
            rowsPerPage={rowsPerPage}
            count={totalCount}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
            renderRow={renderRow}
          />
        </Card>
      </Container>
    </PermissionBasedGuard>
  );
}
